import { useFormik } from 'formik';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { emitUserError, emitUserInfo } from 'shared/constants/globals';
import { ROUTE } from 'shared/constants/routes_old';
import { useSearch } from 'shared/helpers/hooks';
import { apiResetPasswordApply } from 'shared/modules/user/actions';
import { FormBox } from 'shared/Pages/common/Login/components/styles';
import { FormikConfig } from 'formik/dist/types';
import { Button, Link, Typography } from '@mui/material';
import PasswordField from 'shared/Pages/common/Login/components/password-field';
import { APPLY_RESET_PASSWORD_VALIDATION_SCHEMA } from 'shared/Pages/common/Login/constants';

type ApplyResetPasswordFormValues = { password: string; passwordConfirm: string };

const COUNTER_VALUE = 5;

export default function ApplyResetPassword() {
	const { token } = useSearch();
	const [done, setDone] = useState(false);
	const [counter, setCounter] = useState(COUNTER_VALUE);
	const history = useHistory();

	const handleSubmit = useCallback<FormikConfig<ApplyResetPasswordFormValues>['onSubmit']>(
		async ({ password }, { setSubmitting }) => {
			if (token) {
				try {
					const res = await apiResetPasswordApply(token, password);
					emitUserInfo(res);
					setDone(true);
					setInterval(() => {
						setCounter((s) => s - 1);
					}, 1000);
					setTimeout(() => {
						history.push(ROUTE.login);
					}, COUNTER_VALUE * 1000);
				} catch (e) {
					emitUserError((e as Error)?.message, 'Apply reset password');
				} finally {
					setSubmitting(false);
				}
			} else {
				emitUserError('Token has not been provided');
			}
		},
		[history, token]
	);

	const formik = useFormik<ApplyResetPasswordFormValues>({
		initialValues: { password: '', passwordConfirm: '' },
		validationSchema: APPLY_RESET_PASSWORD_VALIDATION_SCHEMA,
		onSubmit: handleSubmit,
	});

	if (done) {
		return (
			<FormBox>
				<Typography variant="h3">Password has been reset</Typography>
				<Typography
					sx={{
						textAlign: 'center',
					}}
				>
					You will be redirected to <Link href={ROUTE.login}>login page</Link> ({counter})
				</Typography>
			</FormBox>
		);
	}

	return (
		<FormBox id="sumbinNewPasswordForm" onSubmit={formik.handleSubmit}>
			<PasswordField
				disabled={formik.isSubmitting}
				formik={formik}
				label="Password"
				name="password"
				placeholder="Create a strong password"
			/>
			<PasswordField
				disabled={formik.isSubmitting}
				formik={formik}
				label="Confirm password"
				name="passwordConfirm"
				placeholder="Confirm new password"
			/>

			<Link
				href={ROUTE.login}
				sx={{
					textAlign: 'end',
				}}
			>
				return to Login page
			</Link>

			<Button
				type="submit"
				size="large"
				fullWidth
				disabled={!formik.dirty || formik.isSubmitting || !formik.isValid}
			>
				Set new password
			</Button>
		</FormBox>
	);
}
